import {ActivatedRoute, Router} from '@angular/router';
import {AfterViewInit, Component, inject, NgZone, OnDestroy, OnInit, signal, ViewEncapsulation} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {DataEntity} from 'octopus-connect';
import {FuseConfirmDialogComponent} from 'fuse-core/components/confirm-dialog/confirm-dialog.component';
import {Subject, Subscription} from 'rxjs';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {currentTimestamp} from 'shared/utils/datetime';
import {fuseAnimations} from 'fuse-core/animations';
import {take, takeUntil} from 'rxjs/operators';
import {LocalCacheService} from '@modules/authentication/core/services/local-cache.service';
import {CachedUser} from '@modules/authentication/core/models/cached-user.type';
import {
    AuthenticationConfigurationService
} from '@modules/authentication/core/config/authentication-configuration.service';
import {CommunicationCenterService} from '@modules/communication-center';
import {AuthenticationModule, AuthenticationService} from '@modules/authentication';
import {FlexModule} from '@angular/flex-layout';
import {LoginFormComponent} from '@modules/authentication/core/login/login-form/login-form.component';
import {LoginLogoComponent} from '@modules/authentication/core/login/login-logo/login-logo.component';
import {FuseDirectivesModule} from 'fuse-core/directives/directives';
import {MatButtonModule} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {NgClass, NgIf} from '@angular/common';
import {RegisterLinkComponent} from '@modules/authentication/core/login/register-link/register-link.component';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {PartnersLogosComponent} from '@modules/authentication/core/login/partners-logos/partners-logos.component';
import {SsoLoginComponent} from '@modules/authentication/core/login/sso-login/sso-login.component';
import {GarLoginComponent} from '@modules/authentication/core/login/gar-login/gar-login.component';
import {AppleAuthResult, AppleAuthService} from './apple-auth.service';
import {FacebookAuthResult, FacebookAuthService} from './facebook-auth.service';
import {ScriptLoaderService} from '../script-loader-service.service';
import {get} from 'lodash-es';
import {buildUrl} from '../../../../../../src/environments/environment';
import {brand} from '../../../../../../src/app/settings/amazon_python';

declare const google: any;

@Component({
    selector: 'app-login',
    standalone: true,
    imports: [
        FlexModule,
        TranslateModule,
        LoginFormComponent,
        LoginLogoComponent,
        FuseDirectivesModule,
        AuthenticationModule,
        MatButtonModule,
        RegisterLinkComponent,
        MatProgressSpinner,
        PartnersLogosComponent,
        SsoLoginComponent,
        GarLoginComponent,
        MatIcon,
        NgIf,
    ],
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {

    private router = inject(Router);
    private dialog = inject(MatDialog);
    private route = inject(ActivatedRoute);
    private translate = inject(TranslateService);
    private localCache = inject(LocalCacheService);
    private authenticationService = inject(AuthenticationService);
    private communicationCenter = inject(CommunicationCenterService);
    private configuration = inject(AuthenticationConfigurationService);

    public renewPassword = false;
    public modeSSO = false;
    public isLoading = false;
    public entButtonAllowed = false; // only allowed for fr language
    public settings = this.configuration.settings;
    public forceLoginValue = signal<string>(null);

    private hideSessions = false;
    private unsubscribeInTakeUntil = new Subject<void>();

    private appleAuthSubscription?: Subscription;
    private facebookAuthSubscription?: Subscription;
    private googleIframeFocusWatcherInterval: ReturnType<typeof setInterval>;
    private lastGoogleIframeFocused = false;
    protected readonly assetbrand = brand;

    private ngZone = inject(NgZone);
    private scriptLoaderService = inject(ScriptLoaderService);
    private appleAuthService: AppleAuthService = inject(AppleAuthService);
    private facebookAuthService: FacebookAuthService = inject(FacebookAuthService);

    /***** GETTERS *****/

    public get displayGARHelper(): boolean {
        return this.configuration.displayGARHelper;
    }

    public get displaySessions(): boolean {
        return this.localCache.hasCachedUsers && !this.hideSessions && !this.renewPassword;
    }

    private get entButtonAllowedWithoutRules(): boolean {
        return this.settings?.entButtonAllowedWithoutRules;
    }

    /***** LIFECYCLE HOOKS *****/

    ngOnInit(): void {
        this.entButtonAllowed = this.configuration.isEntButtonAllowed;

        if (this.route.snapshot.queryParams.code) {
            this.modeSSO = true;
            this.authenticationService.loginSSO(this.route.snapshot.queryParams.code);
        }
    }

    ngAfterViewInit(): void {
        if (!this.activateAuthenticationByThirdParty) {
            return;
        }

        if (this.googleClientId) {
            this.scriptLoaderService.loadGoogleScript()
                .then(() => {
                    this.googleCallBack();
                    this.initGoogleButtonAndCallBackSettings();
                })
                .catch((error) => {
                    console.error('Google script failed to load', error);
                });
            this.startGoogleIframeFocusWatcher();
        } else {
            console.warn('Google sign-in disabled: missing googleClientId setting.');
        }

        if (this.appleClientId && this.appleRedirectUri) {
            this.appleAuthSubscription = this.appleAuthService.initialize({
                clientId: this.appleClientId,
                redirectUri: this.appleRedirectUri,
                buttonId: 'appleid-signin'
            }).subscribe((result: AppleAuthResult) => {
                if (result.token) {
                    this.connect(result.token);
                } else if (result.error) {
                    console.error('Apple sign-in error', result.error);
                }
            });
        }

        this.initFacebookLayer();
    }

    private initFacebookLayer(): void {
        if (this.activateAuthenticationByThirdParty && this.facebookAppId && !this.facebookAuthSubscription) {
            this.facebookAuthSubscription = this.facebookAuthService.initialize({
                appId: this.facebookAppId,
                buttonId: 'facebook-signin-button' // On le laisse pour la config, mais on ne s'en sert plus pour le clic
            }).subscribe((result: FacebookAuthResult) => {
                if (result.token) {
                    this.connect(result.token);
                } else if (result.error) {
                    console.error('Facebook sign-in error', result.error);
                }
            });
        }
    }

    public signInWithFacebook(): void {
        this.facebookAuthService.login();
    }

    /* GOOGLE */
    private googleCallBack() {
        // Définir la callback globale que Google appelle après login
        (window as any).handleCredentialResponse = (response: any) => {
            this.ngZone.run(() => {
                // token google a envoyer au back pour que le back se connecte et renvoi un jwt au front
                const idToken = response.credential;
                this.authenticationService.loginWithGoogle(idToken).pipe(take(1))
                    .subscribe({
                        next: (res) => {
                            this.connect(res?.token);
                        },
                        error: (err) => {
                            console.error('Erreur google connexion:', err);
                        }
                    });
            });
        };
    }

    private initGoogleButtonAndCallBackSettings() {
        google.accounts.id.initialize({
            client_id: this.googleClientId,
            callback: (window as any).handleCredentialResponse
        });

        google.accounts.id.renderButton(
            document.getElementById('google-signin-button'),
            {theme: 'outline', size: 'large'}
        );
    }

    private connect(token: string) {
        this.authenticationService.setTokenData(token);
        this.authenticationService.loginWithToken(token).pipe(take(1)).subscribe({
            next: () => {
                this.authenticationService.navigateAfterLogin();
            },
            error: (err) => {
                console.error('Erreur loginWithToken', err);
            }
        });
    }

    ngOnDestroy(): void {
        this.isLoading = false;
        this.unsubscribeInTakeUntil.next();
        this.unsubscribeInTakeUntil.complete();
        this.appleAuthSubscription?.unsubscribe();
        this.appleAuthService.dispose();
        this.facebookAuthSubscription?.unsubscribe();
        this.facebookAuthService.dispose();
        if (this.googleIframeFocusWatcherInterval) {
            clearInterval(this.googleIframeFocusWatcherInterval);
        }
    }

    /***** LOGIC *****/

    /**
     * login user : with optionnal change password possibility force by rules
     */
    private doLogin(label: string, pass: string): void {
        this.isLoading = true;

        if (label && pass) {
            this.authenticationService
                .authenticateIn(
                    'http',
                    label,
                    pass
                )
                .pipe(takeUntil(this.unsubscribeInTakeUntil))
                .subscribe((user) => {
                    if (
                        this.settings.validateEmailStrategyActivated &&
                        !user.get('email_status')
                    ) {
                        this.authenticationService.logoutFrom('http');
                        this.isLoading = false;
                        return;
                    }

                    if (this.isExpirePassword(user)) {
                        this.renewPassword = true;
                        this.authenticationService.logoutFrom('http');
                        this.isLoading = false;
                        return;
                    }

                    const isTrainer = !!(user.get('role').findIndex((role) => role === 5) >= 0); // tester autrement qu'avec un numero (voir authService)
                    // it's fisrts user trainer connexion and help is active
                    if (
                        user.get('access') &&
                        user.get('first_access') === true &&
                        this.settings.askForHelp &&
                        isTrainer
                    ) {
                        this.help();
                    } else {
                        this.authenticationService.navigateAfterLogin();
                    }
                }, error => {
                    console.error(error);
                    this.isLoading = false;
                });
        }
    }

    /**
     * open modal asking if user need help at first connexion
     */
    private help(): void {
        const data = {
            titleDialog: 'generic.ask.help',
            bodyDialog: 'generic.ask.help.content',
            labelTrueDialog: 'generic.yes',
            labelFalseDialog: 'generic.no',
        };

        this.translate
            .get(data.titleDialog)
            .subscribe((translation: string) => (data.titleDialog = translation));
        this.translate
            .get(data.bodyDialog)
            .subscribe((translation: string) => (data.bodyDialog = translation));
        this.translate
            .get(data.labelTrueDialog)
            .subscribe((translation: string) => (data.labelTrueDialog = translation));
        this.translate
            .get(data.labelFalseDialog)
            .subscribe(
                (translation: string) => (data.labelFalseDialog = translation)
            );

        const confirmDialogRef = this.dialog.open(FuseConfirmDialogComponent, {
            data,
        });

        confirmDialogRef.afterClosed().subscribe((result) => {
            if (result) {
                // help page
                this.router.navigate(['home']);
            } else {
                // normal navigate
                this.authenticationService.navigateAfterLogin();
            }
        });
    }

    /**
     * is user expiration password date is passed
     */
    private isExpirePassword(user: DataEntity): boolean {
        return (
            this.settings.activeChangePasswordStrategy &&
            user.get('expirePassword') &&
            currentTimestamp() >= user.get('expirePassword') &&
            user.get('expirePassword') !== null &&
            user.get('expirePassword') !== undefined
        );
    }


    /***** EVENTS *****/

    public onLoginSession(user: CachedUser): void {
        if (user?.codeid) {
            this.doLogin(`tralacodeid${user.label}`, user.codeid);
        } else {
            if (user) {
                this.forceLoginValue.set(user.label);
            }
            this.hideSessions = true;
        }
    }

    public get googleClientId(): string {
        return get(this.settings, 'googleClientId', '');
    }

    public get activateAuthenticationByThirdParty(): boolean {
        return get(this.settings, 'activateAuthenticationByThirdParty', false);
    }

    public get appleClientId(): string {
        return get(this.settings, 'appleClientId', '');
    }

    public get appleRedirectUri(): string {
        const configured = get(this.settings, 'appleRedirectUri', '');
        if (!configured) {
            return '';
        }

        if (/^https?:\/\//i.test(configured)) {
            return configured;
        }

        const baseUrl = buildUrl(this.assetbrand);
        const sanitizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
        const sanitizedPath = configured.startsWith('/') ? configured : `/${configured}`;
        return `${sanitizedBase}${sanitizedPath}`;
    }

    public get facebookAppId(): string {
        return get(this.settings, 'facebookAppId', '');
    }

    // Chrome browser specific issue: since Google "Sign in" button is embedded in an iframe, we need to check if that iframe is active in order to know if the button is focused
    public isGoogleIframeFocused(): boolean {
        const iframe = document.querySelector('#google-signin-button iframe') as HTMLIFrameElement | null;
        return !!iframe && document.activeElement === iframe;
    }

    private startGoogleIframeFocusWatcher(): void {
        this.ngZone.runOutsideAngular(() => {
            this.googleIframeFocusWatcherInterval = setInterval(() => {
                const now = this.isGoogleIframeFocused();
                if (now !== this.lastGoogleIframeFocused) {
                    this.lastGoogleIframeFocused = now;
                    this.ngZone.run(() => {
                    });
                }
            }, 150);
        });
    }

}